import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/base/EventCard";
import JsonLd from "@/components/base/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";
import { events, categories } from "@/mocks/gossip";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const list = events.filter((e) => e.categorySlug === slug);

  usePageMeta(category ? `${category.name} - 事件时间线复盘合集` : "主题分类未找到", {
    description: category
      ? `${category.name}主题下的全部深扒复盘合集。${category.desc}`
      : "该主题分类不存在。",
    keywords: `${category?.name ?? ""},91吃瓜网,时间线复盘,娱乐八卦`,
    canonicalPath: `/category/${slug}`,
    ogType: "website",
  });

  if (!category) {
    return (
      <>
        <SiteHeader />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
          <i className="ri-folder-unknow-line text-4xl text-foreground-400"></i>
          <h1 className="mt-4 font-heading font-bold text-2xl text-foreground-950">
            这个主题分类不存在
          </h1>
          <Link
            to="/events"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回复盘档案库
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - 事件时间线复盘合集`,
            description: category.desc,
            inLanguage: "zh-CN",
            hasPart: list.map((e) => ({
              "@type": "Article",
              headline: e.title,
              description: e.summary,
              datePublished: e.date,
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://microtransport.cn/" },
              { "@type": "ListItem", position: 2, name: "复盘档案", item: "https://microtransport.cn/events" },
              { "@type": "ListItem", position: 3, name: category.name },
            ],
          },
        ]}
      />
      <SiteHeader />
      <main className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[{ name: "首页", to: "/" }, { name: "复盘档案", to: "/events" }, { name: category.name }]}
          />
        </div>

        <header className="w-full px-4 md:px-8 pt-6 pb-8 border-b border-background-200">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-lg bg-accent-500 text-accent-950">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading font-black text-3xl md:text-[40px] leading-tight text-foreground-950">
                {category.name}
              </h1>
              <p className="mt-2 font-label text-sm md:text-[15px] text-foreground-600 max-w-2xl">
                {category.desc}
              </p>
            </div>
            <span className="md:ml-auto shrink-0 rounded-md border border-background-300 bg-background-100 px-4 py-2 font-label text-sm text-foreground-700">
              共 {list.length} 篇复盘
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/events"
              className="rounded-md border border-background-300 bg-background-100 px-4 py-2 font-label text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`rounded-md px-4 py-2 font-label text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  c.slug === category.slug
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </header>

        <div className="w-full px-4 md:px-8 py-10">
          {list.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-12 text-center">
              <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              <p className="mt-3 font-label text-sm text-foreground-600">
                该分类下暂时还没有复盘档案，我们会持续更新。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          )}

          <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex-1">
              <h2 className="font-heading font-bold text-lg text-foreground-950">
                没找到想看的事件？
              </h2>
              <p className="mt-1.5 font-label text-sm text-foreground-600">
                在档案库里用关键词搜索，或浏览其他主题分区。
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/events"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line"></i>
                去档案库搜索
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 font-label text-sm text-foreground-800 hover:border-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解内容原则
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}